import React, { useState, useCallback } from "react";
import {
  Button,
  Icon,
  Modal,
  Progress,
  Message,
  Form,
  Input,
  Header,
  Segment,
} from "semantic-ui-react";

/**
 * SendToCanvaButton
 *
 * A Semantic UI button that uploads an image to the user's Canva library
 * and optionally creates a new design with it.
 *
 * Props:
 *   imageUrl       (string, required) — Public URL of the image to send
 *   imageName      (string)           — Display name for the asset in Canva
 *   onSuccess      (function)         — Called with { job, design? } on success
 *   onError        (function)         — Called with error object on failure
 *   designType     (string|null)      — If set, also creates a design (e.g. "doc", "presentation", "instagramPost")
 *   showDesignPicker (bool)           — Show a dropdown to let the user pick the design type
 *   buttonProps    (object)           — Extra props spread onto the <Button>
 *   compact        (bool)             — Render a compact icon-only button
 *   apiBase        (string)           — Base path for the backend API (default: "/api")
 */

const STATUS = {
  IDLE: "idle",
  CHECKING_AUTH: "checking_auth",
  UPLOADING: "uploading",
  CREATING_DESIGN: "creating_design",
  SUCCESS: "success",
  ERROR: "error",
};

export function SendToCanvaButton({
  imageUrl,
  imageName = "Uploaded Image",
  onSuccess,
  onError,
  showDesignPicker = false,
  buttonProps = {},
  compact = false,
  apiBase = "https://delivery.hommati.com/api",
}) {
  const [status, setStatus] = useState(STATUS.IDLE);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState(null);

  const [customName, setCustomName] = useState(imageName);

  const resetState = useCallback(() => {
    setStatus(STATUS.IDLE);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    setModalOpen(false);
    // Reset after animation
    setTimeout(resetState, 300);
  }, [resetState]);

  const checkAuth = useCallback(async () => {
    const res = await fetch(`${apiBase}/oauth/status`, { credentials: "include" });
    const data = await res.json();
    return data.authenticated;
  }, [apiBase]);

  const sendToCanva = useCallback(async () => {
    setStatus(STATUS.CHECKING_AUTH);
    setError(null);

    try {
      // 1. Check authentication
      const isAuth = await checkAuth();
      if (!isAuth) {
        const currentUrl = new URL(window.location.href);
        const newBase = "https://delivery.hommati.com";

        const previewReturnTo = new URL(currentUrl.pathname + currentUrl.search, newBase);
        if (currentUrl.hostname === 'delivery.hommati.com') {
          previewReturnTo.searchParams.set('redirect_after', currentUrl.href);
        }

        // Redirect to OAuth — save current page to return to
        window.location.href = `${apiBase}/oauth/authorize?return_to=${encodeURIComponent(previewReturnTo.href)}`;
        return;
      }

      // 2. Upload
      setStatus(STATUS.UPLOADING);
      const body = {
        url: imageUrl,
        name: customName || imageName,
      };

      const res = await fetch(`${apiBase}/assets/send-to-canva`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || `Request failed (${res.status})`);
      }

      if (data.job?.status === "in_progress") {
        // Upload is still processing — the server timed out waiting
        setStatus(STATUS.UPLOADING);
        setError("Upload is still processing. Check your Canva library in a moment.");
        onError?.({ message: "Upload still in progress", data });
        return;
      }

      if (data.job?.status === "failed") {
        throw new Error(data.job.error?.message || "Upload failed in Canva");
      }

      // Success
      setStatus(STATUS.SUCCESS);
      onSuccess?.(data);
    } catch (err) {
      setStatus(STATUS.ERROR);
      setError(err.message || "Something went wrong");
      onError?.(err);
    }
  }, [
    apiBase,
    checkAuth,
    customName,
    imageUrl,
    imageName,
    onError,
    onSuccess,
  ]);

  // --- Simple (no modal) mode ---
  if (!showDesignPicker) {
    const isWorking = [STATUS.CHECKING_AUTH, STATUS.UPLOADING, STATUS.CREATING_DESIGN].includes(
      status
    );

    if (compact) {
      return (
        <Button
          icon
          loading={isWorking}
          color={status === STATUS.SUCCESS ? "green" : "purple"}
          title="Send to Canva"
          onClick={sendToCanva}
          disabled={isWorking || !imageUrl}
          {...buttonProps}
        >
          <Icon name={status === STATUS.SUCCESS ? "check" : "cloud upload"} />
        </Button>
      );
    }

    return (
      <Button
        loading={isWorking}
        color={status === STATUS.SUCCESS ? "green" : "purple"}
        onClick={sendToCanva}
        disabled={isWorking || !imageUrl}
        {...buttonProps}
      >
        <Icon name={status === STATUS.SUCCESS ? "check" : "cloud upload"} />
        {status === STATUS.SUCCESS ? "Sent to Canva!" : "Send to Canva"}
      </Button>
    );
  }

  // --- Modal mode (with design picker) ---
  const isWorking = [STATUS.CHECKING_AUTH, STATUS.UPLOADING, STATUS.CREATING_DESIGN].includes(
    status
  );

  return (
    <>
      <Button
        color="purple"
        onClick={() => {
          resetState();
          setCustomName(imageName);
          setModalOpen(true);
        }}
        disabled={!imageUrl}
        {...buttonProps}
      >
        <Icon name="cloud upload" />
        Send to Canva
      </Button>

      <Modal open={modalOpen} onClose={handleClose} size="small" closeIcon>
        <Header icon="cloud upload" content="Send to Canva" />

        <Modal.Content>
          {/* Image preview */}
          {imageUrl && (
            <Segment textAlign="center" basic>
              <img
                src={imageUrl}
                alt={customName}
                style={{
                  maxWidth: "100%",
                  maxHeight: 200,
                  borderRadius: 8,
                  objectFit: "contain",
                }}
              />
            </Segment>
          )}

          <Form>
            <Form.Field>
              <label>Asset name</label>
              <Input
                value={customName}
                onChange={(e, { value }) => setCustomName(value)}
                placeholder="Name for the asset in Canva"
                disabled={isWorking}
              />
            </Form.Field>

          </Form>

          {/* Progress / status feedback */}
          {isWorking && (
            <Segment basic>
              <Progress
                percent={status === STATUS.UPLOADING ? 60 : 30}
                active
                color="purple"
                size="small"
              >
                {status === STATUS.CHECKING_AUTH && "Checking authentication…"}
                {status === STATUS.UPLOADING && "Uploading to Canva…"}
                {status === STATUS.CREATING_DESIGN && "Creating design…"}
              </Progress>
            </Segment>
          )}

          {status === STATUS.SUCCESS && (
            <Message positive icon>
              <Icon name="check circle" />
              <Message.Content>
                <Message.Header>Sent to Canva!</Message.Header>
                <p>
                  The image is now in your Canva library.
                </p>
              </Message.Content>
            </Message>
          )}

          {status === STATUS.ERROR && (
            <Message negative icon>
              <Icon name="exclamation triangle" />
              <Message.Content>
                <Message.Header>Something went wrong</Message.Header>
                <p>{error}</p>
              </Message.Content>
            </Message>
          )}
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            color="purple"
            onClick={sendToCanva}
            loading={isWorking}
            disabled={isWorking || !imageUrl || status === STATUS.SUCCESS}
          >
            <Icon name="cloud upload" />
            {status === STATUS.SUCCESS ? "Done" : "Send to Canva"}
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}

export default SendToCanvaButton;